import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { FILM_CATEGORIES } from "../constants/catalog";
import ProductCard from "../components/ProductCard";
import ProductQuickView from "../components/ProductQuickView";
import RequestQuoteModal from "../components/RequestQuoteModal";
import { ArrowUpRight, ArrowRight } from "lucide-react";

export default function WindowFilm() {
    const [products, setProducts] = useState([]);
    const [cats, setCats] = useState(FILM_CATEGORIES);
    const [quickView, setQuickView] = useState(null);
    const [quoteFor, setQuoteFor] = useState(null);

    useEffect(() => {
        api.get("/products?category=film").then((r) => setProducts(r.data));
        api.get("/categories?group=film").then((r) => { if (r.data?.length) setCats(r.data); }).catch(() => {});
    }, []);

    const byCat = useMemo(() => {
        const map = {};
        products.forEach((p) => { (map[p.subcategory] ||= []).push(p); });
        return map;
    }, [products]);

    return (
        <div data-testid="window-film-landing">
            {/* HERO */}
            <section className="relative h-[62vh] min-h-[460px] overflow-hidden">
                <img src="https://images.unsplash.com/photo-1708805282706-f44730b7e527?w=2000&q=80" alt="Automotive window film" fetchPriority="high" className="absolute inset-0 w-full h-full object-cover" />
                <div className="absolute inset-0 hero-overlay" />
                <div className="relative h-full max-w-7xl mx-auto px-6 md:px-12 flex flex-col justify-end pb-16">
                    <div className="max-w-3xl animate-fade-up">
                        <div className="flex items-center gap-3 mb-5">
                            <div className="w-12 h-px bg-gold" />
                            <span className="text-[11px] tracking-[0.4em] uppercase text-gold">Automotive Window Film</span>
                        </div>
                        <h1 className="font-display text-4xl md:text-6xl tracking-tighter font-medium leading-[0.98]">Automotive Window Film</h1>
                        <p className="mt-6 text-[#33302C] max-w-xl text-base md:text-lg font-light leading-relaxed">
                            Premium automotive tint films for heat rejection, privacy, UV protection, and a cleaner vehicle look.
                        </p>
                        <div className="mt-8 flex flex-wrap gap-4">
                            <a href="#film-categories" data-testid="hero-shop-film" className="px-8 py-4 bg-gold text-white font-semibold uppercase tracking-[0.2em] text-xs hover:bg-gold-hover transition-colors inline-flex items-center gap-2">Shop Film Rolls <ArrowUpRight size={16} /></a>
                            <Link to="/contact" data-testid="hero-install-quote" className="px-8 py-4 border border-[#8C5A3C]/40 text-[#1A1A1A] uppercase tracking-[0.2em] text-xs hover:border-gold hover:text-gold transition-colors">Get Installation Quote</Link>
                            <Link to="/contact" data-testid="hero-dealer-pricing" className="px-8 py-4 border border-[#8C5A3C]/40 text-[#1A1A1A] uppercase tracking-[0.2em] text-xs hover:border-gold hover:text-gold transition-colors">Request Dealer Pricing</Link>
                        </div>
                    </div>
                </div>
            </section>

            {/* FILM CATEGORY SECTIONS */}
            <div id="film-categories" className="max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-24 space-y-20">
                {FILM_CATEGORIES.map((cat) => {
                    const items = byCat[cat.slug] || [];
                    return (
                        <section key={cat.slug} data-testid={`film-section-${cat.slug}`}>
                            <div className="flex items-end justify-between gap-6 flex-wrap mb-8 border-b border-[#E2D4C1] pb-5">
                                <div>
                                    <div className="text-[11px] tracking-[0.4em] uppercase text-gold mb-2">{cat.slug.replace("-", " ")}</div>
                                    <h2 className="font-display text-3xl md:text-4xl tracking-tight">{cat.name}</h2>
                                    <p className="text-[#33302C] max-w-2xl mt-2 text-sm">{cat.desc}</p>
                                </div>
                                <Link to={`/automotive-window-film/${cat.slug}`} data-testid={`film-view-more-${cat.slug}`} className="text-[11px] tracking-[0.2em] uppercase text-gold hover:text-gold-hover inline-flex items-center gap-1">
                                    View More <ArrowRight size={13} />
                                </Link>
                            </div>
                            {items.length === 0 ? (
                                <div className="border border-[#E2D4C1] bg-[#F7F3EB] py-16 text-center text-[#6B645C] text-sm">Coming soon.</div>
                            ) : (
                                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
                                    {items.slice(0, 3).map((p, i) => (
                                        <ProductCard key={p.id} product={p} index={i} onQuickView={setQuickView} onRequestQuote={setQuoteFor} />
                                    ))}
                                </div>
                            )}
                        </section>
                    );
                })}
            </div>

            <ProductQuickView product={quickView} open={!!quickView} onOpenChange={(v) => !v && setQuickView(null)} onRequestQuote={setQuoteFor} />
            <RequestQuoteModal product={quoteFor} open={!!quoteFor} onOpenChange={(v) => !v && setQuoteFor(null)} />
        </div>
    );
}
